'use client';

import React, { useEffect, useRef, useState } from 'react';
import * as LucideIcons from '@/lib/icons';
import { preventOrphans } from '@/lib/typography-utils';

interface StatProps {
  number: string;
  label: string;
  icon?: string;
  gradientText?: boolean;
  animate?: boolean;
  prefix?: string;
  suffix?: string;
  duration?: number;
  variant?: 'default' | 'highlight' | 'gradient';
}

export const Stat: React.FC<StatProps & {columnCount?: number; labelSizeClass?: string}> = ({ 
  number, 
  label, 
  icon,
  gradientText = false,
  animate = true,
  prefix = '',
  suffix = '',
  duration = 2000,
  variant = 'default',
  columnCount = 4,
  labelSizeClass
}) => {
  const [displayNumber, setDisplayNumber] = useState(animate ? '0' : number);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statRef = useRef<HTMLDivElement>(null);
  
  // Convert icon string to PascalCase to match Lucide component names
  const iconName = icon ? icon.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join('') : '';
  
  const IconComponent = iconName ? (LucideIcons as any)[iconName] : null;
  
  useEffect(() => {
    if (!animate || hasAnimated) return;
    
    // Advanced number parsing - handles decimals, percentages, currencies, etc.
    const parseNumberFromString = (str: string) => {
      // First check if it's a range like "30-50%" - just return the string as-is
      if (str.includes('-') && !str.startsWith('-')) {
        setDisplayNumber(str);
        return null;
      }
      
      // Extract the numeric part with K/M suffix handling
      const hasK = str.toUpperCase().includes('K');
      const hasM = str.toUpperCase().includes('M');
      const hasDollar = str.includes('$');
      const hasPercent = str.includes('%');
      const hasPlus = str.includes('+');
      
      // Clean string for number extraction (keep K/M for now)
      let cleaned = str.replace(/[$,\s%+]/g, '');
      if (hasK) cleaned = cleaned.replace(/K/gi, '');
      if (hasM) cleaned = cleaned.replace(/M/gi, '');
      
      // Extract number with optional decimal
      const match = cleaned.match(/([\d]+(?:\.\d+)?)/);
      if (!match) return null;
      
      const numericValue = parseFloat(match[1]);
      const isDecimal = str.includes('.');
      const hasComma = str.includes(',') && numericValue >= 1000;
      
      // Convert K/M to actual numbers for animation
      let actualValue = numericValue;
      if (hasK) actualValue = numericValue * 1000;
      if (hasM) actualValue = numericValue * 1000000;
      
      return {
        value: actualValue,
        displayValue: numericValue,
        isDecimal,
        hasComma,
        hasPlus,
        hasPercent,
        hasDollar,
        hasK,
        hasM,
        decimalPlaces: isDecimal ? (match[1].split('.')[1]?.length || 1) : 0
      };
    };
    
    const numberInfo = parseNumberFromString(number);
    if (!numberInfo) {
      setDisplayNumber(number);
      return;
    }
    
    // Intersection Observer for scroll-triggered animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            let animationFrameId: number;
            const startTime = performance.now();
            
            const formatNumber = (value: number) => {
              let displayValue = value;
              
              // Convert back to K/M format
              if (numberInfo.hasM) displayValue = value / 1000000;
              if (numberInfo.hasK) displayValue = value / 1000;
              
              // Handle decimals properly
              let formatted: string;
              if (numberInfo.isDecimal) {
                formatted = displayValue.toFixed(numberInfo.decimalPlaces);
              } else {
                formatted = Math.floor(displayValue).toString();
              }
              
              // Add commas for large numbers (only if original had them)
              if (numberInfo.hasComma && displayValue >= 1000 && !numberInfo.hasK && !numberInfo.hasM) {
                const parts = formatted.split('.');
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                formatted = parts.join('.');
              }
              
              // Add prefixes/suffixes in correct order
              if (numberInfo.hasDollar) formatted = '$' + formatted;
              if (numberInfo.hasK) formatted += 'K';
              if (numberInfo.hasM) formatted += 'M';
              if (numberInfo.hasPlus) formatted += '+';
              if (numberInfo.hasPercent) formatted += '%';
              
              return formatted;
            };
            
            const animate = (currentTime: number) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              // Smooth easing function - less aggressive
              const easeOutCubic = 1 - Math.pow(1 - progress, 3);
              const currentValue = numberInfo.value * easeOutCubic;
              
              setDisplayNumber(formatNumber(currentValue));
              
              if (progress < 1) {
                animationFrameId = requestAnimationFrame(animate);
              } else {
                // Ensure final value is exactly the original
                setDisplayNumber(number);
              }
            };
            
            animationFrameId = requestAnimationFrame(animate);
            
            // Cleanup function
            return () => {
              if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
              }
            };
          }
        });
      },
      { 
        threshold: 0.3, // Trigger earlier for smoother experience
        rootMargin: '50px' // Start animation before element is fully visible
      }
    );
    
    if (statRef.current) {
      observer.observe(statRef.current);
    }
    
    return () => {
      observer.disconnect();
    };
  }, [number, animate, hasAnimated, duration]);
  
  // Premium variant styles for individual stats
  const getStatStyles = () => {
    switch(variant) {
      case 'highlight':
        return {
          wrapper: 'bg-gradient-to-br from-primary/5 via-leadpops-pink/5 to-purple-50 border-2 border-primary/20',
          iconBg: 'icon-gradient-primary',
          numberColor: 'text-gradient',
          shadow: 'shadow-lg hover:shadow-2xl shadow-primary/10'
        };
      case 'gradient':
        return {
          wrapper: 'bg-gradient-to-br from-white via-gray-50 to-primary/5 border border-gray-200',
          iconBg: 'icon-gradient-primary',
          numberColor: gradientText ? 'text-gradient' : 'text-gray-900',
          shadow: 'shadow-xl hover:shadow-2xl'
        };
      default:
        return {
          wrapper: 'bg-white border-2 border-gray-100',
          iconBg: 'icon-gradient-primary',
          numberColor: gradientText ? 'text-gradient' : 'text-gray-900',
          shadow: 'shadow-lg hover:shadow-xl'
        };
    }
  };
  
  const styles = getStatStyles();
  
  // Get responsive number size based on column count - improved sizing
  const getNumberSize = () => {
    if (columnCount <= 2) return 'text-3xl sm:text-4xl lg:text-5xl xl:text-6xl';
    if (columnCount === 3) return 'text-2xl sm:text-3xl lg:text-4xl xl:text-5xl';
    if (columnCount === 4) return 'text-xl sm:text-2xl lg:text-3xl xl:text-4xl'; // Perfect for 4 columns
    return 'text-lg sm:text-xl lg:text-2xl xl:text-3xl'; // 5+ columns
  };
  
  return (
    <div 
      ref={statRef} 
      className={`
        stat-card group relative block
        ${styles.wrapper}
        rounded-md p-4 lg:p-6
        ${styles.shadow}
        transform transition-all duration-300 ease-out
        hover:scale-[1.01] hover:-translate-y-1
        overflow-hidden
        ${columnCount <= 2 ? 'min-h-[140px] lg:min-h-[160px]' : 
          columnCount === 3 ? 'min-h-[130px] lg:min-h-[150px]' :
          'min-h-[120px] lg:min-h-[140px]'} 
        flex flex-col justify-center
        prose-safe min-w-0 w-full
      `}
    >
      {/* Premium gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      
      {/* Content container */}
      <div className="relative z-10">
        {/* Icon - properly centered */}
        {IconComponent && (
          <div className="mb-4 flex justify-center">
            <div className={`
              w-12 h-12 rounded-md flex items-center justify-center
              ${styles.iconBg}
              shadow-lg group-hover:shadow-xl
              transform transition-all duration-300
              group-hover:scale-110 group-hover:rotate-3
            `}>
              <IconComponent className="w-7 h-7 text-white drop-shadow-sm" />
            </div>
          </div>
        )}
        
        {/* Number - responsive size based on column count */}
        <div className={`
          ${getNumberSize()} font-black mb-2
          transition-all duration-300 group-hover:scale-105
          ${styles.numberColor}
          leading-none text-center
          px-2
          min-h-[1.2em]
          flex items-center justify-center
        `}>
          <span className="tabular-nums font-feature-settings-['tnum'] whitespace-nowrap">
            {prefix}{displayNumber}{suffix}
          </span>
        </div>
        
        {/* Label - consistent sizing across all stats in the same bar */}
        <div className={`
          font-semibold text-gray-600 uppercase text-center px-1 min-w-0
          ${labelSizeClass || (columnCount <= 2 ? 'text-sm sm:text-base lg:text-lg tracking-wide' : 
            columnCount === 3 ? 'text-xs sm:text-sm lg:text-base tracking-normal' :
            columnCount === 4 ? 'text-[10px] sm:text-xs lg:text-sm tracking-tight' :
            'text-[9px] sm:text-[10px] lg:text-xs tracking-tight')}
          leading-tight whitespace-normal break-normal
        `}>
          {preventOrphans(label)}
        </div>
      </div>
      
      {/* Premium shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none" />
    </div>
  );
};

interface StatsBarProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'elegant' | 'bold' | 'neon' | 'minimal';
  layout?: 'showcase' | 'grid' | 'marquee' | 'stacked';
  animate?: boolean;
  columns?: 2 | 3 | 4 | 5;
}

export const StatsBar: React.FC<StatsBarProps> = ({ 
  children, 
  className = '',
  variant = 'elegant',
  layout = 'showcase',
  animate = true,
  columns
}) => {
  // Count children for automatic column sizing - MOVED FIRST
  const childCount = React.Children.count(children);
  const actualColumns = columns || childCount;
  
  // Analyze all labels to determine consistent sizing
  const getLabelSizeClass = () => {
    let maxLabelLength = 0;
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === Stat) {
        const label = (child.props as any).label || '';
        if (label.length > maxLabelLength) {
          maxLabelLength = label.length;
        }
      }
    });
    
    // Determine size based on longest label AND column count
    if (actualColumns <= 2) {
      return 'text-sm sm:text-base lg:text-lg tracking-wide';
    } else if (actualColumns === 3) {
      if (maxLabelLength > 20) return 'text-[10px] sm:text-xs lg:text-sm tracking-tight';
      return 'text-xs sm:text-sm lg:text-base tracking-normal';
    } else if (actualColumns === 4) {
      if (maxLabelLength > 15) return 'text-[9px] sm:text-[11px] lg:text-xs tracking-tight';
      return 'text-[10px] sm:text-xs lg:text-sm tracking-tight';
    } else {
      return 'text-[9px] sm:text-[10px] lg:text-xs tracking-tight';
    }
  };
  
  const labelSizeClass = getLabelSizeClass();
  
  // Pass animate prop, column count, and label size to all Stat children
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Stat) {
      return React.cloneElement(child as React.ReactElement<StatProps & {columnCount?: number; labelSizeClass?: string}>, {
        animate: child.props.animate !== undefined ? child.props.animate : animate,
        columnCount: actualColumns,
        labelSizeClass: labelSizeClass
      });
    }
    return child;
  });
  
  // Determine grid columns - improved responsive behavior
  const getGridClass = () => {
    switch (actualColumns) {
      case 2:
        return 'grid-cols-1 sm:grid-cols-2';
      case 3:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
      case 4:
        return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'; // Better 4-column layout
      case 5:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5';
      default:
        return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4';
    }
  };
  
  // Container variant styles - simplified to not interfere with cards
  const variants = {
    elegant: {
      wrapper: '',
      containerClass: ''
    },
    bold: {
      wrapper: 'bg-gradient-to-br from-primary/5 to-leadpops-pink/5 p-8 rounded-3xl',
      containerClass: '[&_.stat-card]:bg-white [&_.stat-card]:border-2 [&_.stat-card]:border-white/50 [&_.stat-card]:shadow-2xl'
    },
    neon: {
      wrapper: 'bg-gradient-to-br from-gray-900 to-black p-8 rounded-3xl',
      containerClass: '[&_.stat-card]:bg-gray-800 [&_.stat-card]:border-2 [&_.stat-card]:border-primary/30 [&_.text-gray-900]:text-white [&_.text-gray-600]:text-gray-400'
    },
    minimal: {
      wrapper: '',
      containerClass: '[&_.stat-card]:shadow-sm [&_.stat-card]:border [&_.stat-card]:border-gray-200'
    }
  };
  
  const variantStyles = variants[variant];
  
  // Grid gap based on column count and layout - improved spacing
  const getGapClass = () => {
    const baseGap = actualColumns <= 2 ? 'gap-6 lg:gap-8' : 
                   actualColumns === 3 ? 'gap-5 lg:gap-7' : 
                   'gap-4 lg:gap-6'; // 4+ columns
    
    switch(layout) {
      case 'showcase':
        return baseGap;
      case 'grid':
        return actualColumns <= 3 ? 'gap-5 lg:gap-6' : 'gap-4 lg:gap-5';
      case 'stacked':
        return 'gap-4';
      default:
        return baseGap;
    }
  };
  
  if (layout === 'stacked') {
    return (
      <div className={`${variantStyles.wrapper} ${variantStyles.containerClass} ${className}`}>
        <div className={`flex flex-col ${getGapClass()}`}>
          {enhancedChildren}
        </div>
      </div>
    );
  }
  
  if (layout === 'marquee') {
    return (
      <div className={`${variantStyles.wrapper} ${variantStyles.containerClass} overflow-hidden ${className}`}>
        <div className="flex gap-8 animate-scroll">
          {enhancedChildren}
          {/* Duplicate for seamless scroll */}
          {enhancedChildren}
        </div>
      </div>
    );
  }
  
  // Default grid layout - clean and simple to let cards shine
  return (
    <div className={`${variantStyles.wrapper} ${variantStyles.containerClass} my-12 prose-safe w-full ${className}`}>
      <div className={`grid ${getGridClass()} ${getGapClass()}`}>
        {enhancedChildren}
      </div>
    </div>
  );
};